import { HStack, Tag, TagLabel, TagLeftIcon, Text, VStack } from "@chakra-ui/react";
import type { JobListItem } from "@models/Job";
import { getHumanReadableCompetences, getJobStatusTagDetails } from "@utils/utils";
import Chevron from "@components/icons/Chevron";
import Lock from "@components/icons/Lock";
import moment from "moment";
import { useOrganisation } from "@api/hooks";

interface Props {
	job: JobListItem;
	onClick?: () => any;
	/** Color of the divider above the row, see `getDividerAbove` */
	dividerAbove: string;
	/** Only set on the last row */
	dividerBelow?: string;
}

const DANGER_STATUSES: JobListItem["status"][] = ["DECLINED", "CANCELLED", "DELETED"];
const NEUTRAL_DIVIDER = "#E2E8F0";

/** Same status colors as the dashboard's job list */
const getRowBackground = (status: JobListItem["status"]) => {
	if (status === "BOOKED") return "#d7f4e4";
	if (DANGER_STATUSES.includes(status)) return "#FFEBEB";
	if (status === "BOOKED_WITH_UNMANNED_SHIFTS") return "#fffcdb";
	return undefined;
};

/** Darker shade of a colored row's background, used for its dividers */
const getRowBorder = (status: JobListItem["status"]) => {
	if (status === "BOOKED") return "#a3d7b9";
	if (DANGER_STATUSES.includes(status)) return "#f0b0b0";
	if (status === "BOOKED_WITH_UNMANNED_SHIFTS") return "#e3d98a";
	return undefined;
};

/**
 * Divider above a row, like the dashboard's job list: darkened to the status
 * shade when this row or the row above is colored (danger wins), so a colored
 * block is framed top and bottom; neutral between two white rows.
 */
export const getDividerAbove = (job: JobListItem, previous?: JobListItem) => {
	const statuses = [job.status, previous?.status].filter((s): s is JobListItem["status"] => !!s);
	const danger = statuses.find((s) => DANGER_STATUSES.includes(s));
	return getRowBorder(danger ?? job.status) ?? (previous && getRowBorder(previous.status)) ?? NEUTRAL_DIVIDER;
};

/** Divider below the last row */
export const getDividerBelow = (job: JobListItem) => getRowBorder(job.status) ?? NEUTRAL_DIVIDER;

export const JobRow: React.FC<Props> = ({ job, onClick, dividerAbove, dividerBelow }) => {
	const { organisation } = useOrganisation();

	if (!organisation) {
		return null;
	}

	const { text, backgroundColor, color, iconType } = getJobStatusTagDetails(job);
	const rowBackground = getRowBackground(job.status) ?? "#FFFFFF";

	return (
		<HStack
			width={"100%"}
			onClick={onClick}
			justifyContent={"space-between"}
			cursor={"pointer"}
			bg={rowBackground}
			borderTop={`1px solid ${dividerAbove}`}
			borderBottom={dividerBelow ? `1px solid ${dividerBelow}` : undefined}
			px={{ base: 3, md: 4 }}
			py={3}
			spacing={{ base: 2, md: 3 }}
			_hover={{ filter: "brightness(0.97)" }}
		>
			<VStack align={"flex-start"} spacing={1}>
				<Text fontSize={{ base: "14px", md: "16px" }} fontWeight={400} color={"#999999"} m={0}>
					Uppdrag {job.orderNumber}
				</Text>
				<Text fontSize={{ base: "14px", md: "16px" }} fontWeight={500} m={0}>
					{job.customer.name}
				</Text>
				<Text fontSize={{ base: "14px", md: "16px" }} fontWeight={400} m={0}>
					{`${moment(job.startDate)
						.locale("sv")
						.format("ddd D/M")
						.replace(/^\w/, (c) => c.toUpperCase())} - ${
						job.competence && getHumanReadableCompetences(job.competence, organisation.competences)
					}`}
				</Text>
			</VStack>
			<HStack flexShrink={0}>
				<Tag size={"sm"} variant="subtle" bg={backgroundColor} borderRadius={"2px"}>
					{iconType && <TagLeftIcon boxSize="12px" as={getIcon(iconType)} />}
					<TagLabel color={color}>{text}</TagLabel>
				</Tag>
				<Chevron />
			</HStack>
		</HStack>
	);
};

const getIcon = (icon: string): React.FC => {
	if (icon === "LOCK") {
		const LockIcon: React.FC = () => (
			<div style={{ marginRight: "5px" }}>
				<Lock size={12} />
			</div>
		);
		LockIcon.displayName = "LockIcon"; // Add displayName property
		return LockIcon;
	}
	const EmptyIcon: React.FC = () => <span></span>;
	EmptyIcon.displayName = "EmptyIcon"; // Add displayName property
	return EmptyIcon;
};
